import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import {
  Adhd,
  Blind,
  CognitiveLearning,
  ColorBlind,
  Dyslexia,
  LowVission,
  MotorImpaired,
  SiezerEplitic,
} from "../../assets";
import { useSettings } from "../../context/SettingsContext";
import profileLogo from "../../assets/image/images.png";
import { Accordion } from "../Accordion";
import { ToggleSwitch } from "../ToggleSwitch";
import "./AccessibilityProfiles.css";
import { postFeatureUnselectMessage } from "../../utils/message.helper";
import { Feature, Features } from "../../constants/features";
import { useKeyboardShortcuts } from "../../hooks";
import { BaseOption } from "../../types/options";

interface AccessibilityProfileOption extends BaseOption {
  icon: React.ReactNode;
}

const accessibilityProfilesOptionList: AccessibilityProfileOption[] = [
  {
    id: Features.PROFILE_BLIND,
    label: "blind",
    icon: <Blind />,
  },
  {
    id: Features.PROFILE_LOW_VISION,
    label: "low_vision",
    icon: <LowVission />,
  },
  {
    id: Features.PROFILE_COLOR_BLIND,
    label: "color_blind",
    icon: <ColorBlind />,
  },
  {
    id: Features.PROFILE_SEIZURE_EPILEPTIC,
    label: "seizure",
    icon: <SiezerEplitic />,
  },
  {
    id: Features.PROFILE_ADHD,
    label: "adhd",
    icon: <Adhd />,
  },
  {
    id: Features.PROFILE_MOTOR_IMPAIRED,
    label: "motor",
    icon: <MotorImpaired />,
  },
  {
    id: Features.PROFILE_DYSLEXIA,
    label: "dyslexia",
    icon: <Dyslexia />,
  },
  {
    id: Features.PROFILE_COGNITIVE_LEARNING,
    label: "cognitive",
    icon: <CognitiveLearning />,
  },
];

const AccessibilityProfiles = () => {
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Feature | null>(() => {
    const stored = localStorage.getItem("selectedAccessibility");
    try {
      return stored && stored !== "undefined" ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Failed to parse widgetData from localStorage:", error);
      return null;
    }
  });
  const {
    setSelectedCheckBox,
    buttonColor,
    widgetDataSettings,
    oversizeWidget,
  } = useSettings();
  const { t } = useTranslation();

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(
      "selectedAccessibility",
      JSON.stringify(selectedOption)
    );
  }, [selectedOption]);

  const toggleOption = (id: Feature) => {
    setSelectedOption((prev) => {
      if (prev === id) {
        setSelectedCheckBox({
          selected: null,
          unSelected: id,
        });
        return null;
      } else {
        setSelectedCheckBox({
          selected: id,
          unSelected: prev,
        });
        return id;
      }
    });
  };

  useKeyboardShortcuts([
    { key: "1", altKey: true, ctrlKey: true, callback: () => toggleOption(Features.PROFILE_BLIND) },
    { key: "2", altKey: true, ctrlKey: true, callback: () => toggleOption(Features.PROFILE_LOW_VISION) },
    { key: "3", altKey: true, ctrlKey: true, callback: () => toggleOption(Features.PROFILE_COLOR_BLIND) },
    { key: "4", altKey: true, ctrlKey: true, callback: () => toggleOption(Features.PROFILE_SEIZURE_EPILEPTIC) },
    { key: "5", altKey: true, ctrlKey: true, callback: () => toggleOption(Features.PROFILE_ADHD) },
    { key: "6", altKey: true, ctrlKey: true, callback: () => toggleOption(Features.PROFILE_MOTOR_IMPAIRED) },
    { key: "7", altKey: true, ctrlKey: true, callback: () => toggleOption(Features.PROFILE_COGNITIVE_LEARNING) },
    { key: "8", altKey: true, ctrlKey: true, callback: () => toggleOption(Features.PROFILE_DYSLEXIA) },
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isSectionVisible) return;

      if (event.key === "Enter") {
        const focusableInputs = inputRefs.current.filter(Boolean);
        const currentIndex = focusableInputs.findIndex(
          (el) => el === document.activeElement
        );

        if (currentIndex !== -1) {
          event.preventDefault();
          const option = accessibilityProfilesOptionList[currentIndex];
          toggleOption(option.id);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSectionVisible]);

  useEffect(() => {
    const handleReset = async (event: MessageEvent) => {
      if (event.data?.action === "RESET") {
        setIsSectionVisible(false);
        setSelectedOption(null);
        localStorage.setItem("selectedAccessibility", JSON.stringify(null));
        setSelectedCheckBox({
          selected: null,
          unSelected: selectedOption,
        });

        const unselectedItems: Feature[] = [
          Features.COLOR_SATURATION,
          Features.CONTENT_DYSLEXIC_FRIENDLY,
          Features.NAVIGATION_BIG_CURSOR,
          Features.NAVIGATION_TOOLTIPS,
          Features.NAVIGATION_PAUSE_ANIMATION,
          Features.NAVIGATION_READING_MASK,
          Features.NAVIGATION_READING_GUIDE,
          Features.COLOR_SMART_CONTRAST,
          Features.NAVIGATION_SCREEN_READER,
          Features.CONTENT_BIGGER_TEXT,
          Features.COLOR_HIGH_SATURATION,
          Features.COLOR_LOW_SATURATION,
          Features.PROFILE_MOTOR_IMPAIRED,
          Features.NAVIGATION_VOICE_NAVIGATION,
        ];

        for (const item of unselectedItems) {
          postFeatureUnselectMessage(item);
        }
      }
    };

    window.addEventListener("message", handleReset);
    return () => window.removeEventListener("message", handleReset);
  }, [selectedOption, setSelectedCheckBox]);

  const isProfilesEnabled =
    widgetDataSettings?.settings?.advanced?.profilesEnabled || true;

  if (!isProfilesEnabled) {
    return null;
  }

  return (
    <Accordion
      ref={containerRef}
      title={t("accessibility_profiles")}
      icon={<img src={profileLogo} alt="" className="h-6 w-6 ml-1" />}
      isOpen={isSectionVisible}
      onToggle={() => setIsSectionVisible((v) => !v)}
      variant="card"
    >
      <>
        <div className="space-y-2">
          {accessibilityProfilesOptionList.map(
            ({ id, label, icon }, index) => (
              <div
                key={id}
                onClick={() => toggleOption(id)}
                className={twMerge(
                  clsx(
                    "flex items-center justify-between bg-[#F2F7FA] p-3 rounded-[20.5px] cursor-pointer",
                    selectedOption === id ? "bg-blue-100 selected-option" : ""
                  )
                )}
              >
                <ToggleSwitch
                  ref={(el) => (inputRefs.current[index] = el)}
                  checked={selectedOption === id}
                  onChange={(e) => {
                    e.stopPropagation(); // keep to prevent accordion toggle
                    toggleOption(id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  tabIndex={isSectionVisible ? 0 : -1}
                  containerClassName=""
                  style={{
                    backgroundColor:
                      selectedOption === id ? buttonColor : "#ddd",
                  }}
                />
                <p
                  className={clsx(
                    "boxText font-poppins font-medium leading-[30.77px] tracking-[0.13px] text-center",
                    oversizeWidget ? "!font-semibold" : undefined
                  )}
                >
                  {t(label)}
                </p>
                {id === Features.PROFILE_BLIND ? (
                  <Blind />
                ) : typeof icon === "string" ? (
                  <img
                    src={icon as string}
                    alt={`${label} Icon`}
                    className="w-10 h-10 rounded-full p-1"
                  />
                ) : (
                  icon
                )}
              </div>
            )
          )}
        </div>
        <div className="pb-[10px]" />
      </>
    </Accordion>
  );
};

export default AccessibilityProfiles;
